import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errText } from "@/lib/api";

export const AccessControlCard = () => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/access/emails");
      setData(res.data);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(errText(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/access/emails", { email: email.trim(), note: note.trim() || null });
      toast.success(`${email.trim()} may now sign in`);
      setEmail("");
      setNote("");
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    try {
      const { data: res } = await api.delete(`/access/emails/${encodeURIComponent(item.email)}`);
      toast.success(
        res.sessions_revoked
          ? `${item.email} removed — ${res.sessions_revoked} session(s) signed out`
          : `${item.email} removed`,
      );
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (denied || !data) return null;

  return (
    <section className="surface p-6 lg:p-8" data-testid="access-control-section">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <ShieldCheck className="h-5 w-5" /> Who can get in
      </h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        These chambers are private. Only the email IDs below can sign in or register — anyone else is
        turned away at the Google sign-in. Removing an email signs that person out immediately.
      </p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Input
          data-testid="whitelist-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && add()}
          placeholder="name@example.com"
          className="rounded-sm h-12 text-base sm:max-w-sm"
        />
        <Input
          data-testid="whitelist-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Who is this? (optional)"
          className="rounded-sm h-12 text-base sm:max-w-xs"
        />
        <Button
          data-testid="whitelist-add-btn"
          onClick={add}
          disabled={busy}
          className="rounded-full h-12 px-6"
        >
          <UserPlus className="h-5 w-5 mr-2" /> Allow
        </Button>
      </div>

      <div className="mt-6 divide-y divide-border" data-testid="whitelist-list">
        {data.emails.map((item) => (
          <div
            key={item.email}
            data-testid={`whitelist-row-${item.email}`}
            className="py-3.5 flex flex-wrap items-center gap-3 justify-between group"
          >
            <div className="min-w-0">
              <p className="text-base font-semibold truncate">
                {item.name || item.email}
                {item.is_owner ? (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Lock className="h-3 w-3" /> Owner
                  </span>
                ) : null}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {[item.email, item.note, item.registered ? "signed in before" : "not yet registered"]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            {item.is_owner ? null : (
              <button
                data-testid={`whitelist-remove-${item.email}`}
                onClick={() => remove(item)}
                className="h-10 w-10 grid place-items-center rounded-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
                aria-label={`Remove ${item.email}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
